import { createSlice, createAction } from '@reduxjs/toolkit';

const initialState = {
  selectedProducts: [],
  deleteModalOpen: false,
  form: {
    formData: {
      name: '',
      description: '',
      productType: 'designer',
      isDigitalOnline: false,
      isLocalService: false,
      serviceRadius: 10,
      stock: '',
      price: '',
      currency: 'USD', // Default currency
      photos: [],
      coverImageIndex: 0,
      forSell: false,
      forRent: false,
      openToSwap: false,
      openToPickUp: false,
      openToShipping: false,
      pickUpAddress: null,
      selectedAddressId: '',
      shippingAddress: null,
      selectedShippingAddressId: '',
      useShippingForPickup: false,
      rentPriceAndTime: [],
      requireDeposit: false,
      rentDeposit: '',
      category: '',
      size: '',
      customSizeDetails: '',
      color: '',
      condition: '',
      material: '',
      brand: '',
      parcelInfo: {
        length: '',
        width: '',
        height: '',
        weight: '',
        units: { distance: 'in', mass: 'lb' }
      },
      expectedCurrencyForOrigin: null,
    },
    editingId: null,
    fieldErrors: {
      name: '',
      description: '',
      productType: '',
      stock: '',
      price: '',
      currency: '',
      photos: '',
      category: '',
      size: '',
      color: '',
      condition: '',
      pickUpAddress: '',
      deliveryOptions: '',
      rentPriceAndTime: '',
      rentDeposit: '',
      material: '',
      brand: '',
      serviceRadius: '',
      parcelInfo: {}
    }
  }
};

// Action to use outside of slice (used by product API)
export const resetSelectedProducts = createAction('listing/resetSelectedProducts');

// Helper function to prepare form data for submission to API
export const prepareFormDataForSubmit = (formData, userId) => {
  // Get product type for conditional processing
  const isVirtualFashion = formData.productType === 'virtual';
  const isOthersType = formData.productType === 'others';
  const isDigitalOnline = isOthersType && formData.isDigitalOnline;
  const isLocalService = isOthersType && formData.isLocalService;
  
  // Handle simplified product types (virtual or digital online) with similar behavior
  const isSimplifiedProduct = isVirtualFashion || isDigitalOnline;
  
  // Create the base data object
  const data = {
    name: formData.name,
    description: formData.description,
    productType: formData.productType,
    isDigitalOnline: formData.isDigitalOnline || false,
    isLocalService: formData.isLocalService || false,
    serviceRadius: formData.serviceRadius || 10,
    
    // ALWAYS include a category regardless of product type
    // This is a workaround for Strapi v3's entity validation
    category: formData.category || 'other',
    
    // Only include size and color for non-simplified products
    ...(!isSimplifiedProduct && !isLocalService ? {
      size: formData.size,
      customSizeDetails: formData.size === 'custom' ? formData.customSizeDetails : '',
      color: formData.color,
    } : {}),
    
    // Handle condition: required for sharing, optional otherwise
    ...(formData.productType === 'sharing' || (formData.condition && formData.condition !== '') ? { 
      condition: formData.condition 
    } : {}),
    
    // Material is optional - only include if it's not an empty string or null
    ...(formData.material && formData.material !== '' ? { 
      material: formData.material 
    } : {}),
    
    // Brand is optional - only include if it's not an empty string or null
    ...(formData.brand && formData.brand !== '' ? { 
      brand: formData.brand 
    } : {}),
    
    // For simplified products set stock to high value automatically
    stock: isSimplifiedProduct || isLocalService ? 999 : (parseInt(formData.stock) || 1),
    
    // For virtual fashion and digital/online, always include price
    // For others, only include price if the item is for sale
    ...(isSimplifiedProduct || formData.forSell ? { 
      price: parseInt(formData.price) || 0,
      currency: formData.currency || 'USD'
    } : { 
      price: 0,
      currency: formData.currency || 'USD'
    }),
    
    photos: formData.photos,
    coverImageIndex: Math.max(0, formData.coverImageIndex || 0), // Ensure coverImageIndex is never negative
    
    // For simplified products, force forSell to true and other options to false
    // For local services, force forRent to true and other options to false
    ...(isSimplifiedProduct ? {
      forSell: true,
      forRent: false,
      openToSwap: false,
      openToPickUp: false,
      openToShipping: false,
    } : isLocalService ? {
      forSell: false,
      forRent: true,
      openToSwap: false,
      openToPickUp: true,  // Local services need pickup address for service location
      openToShipping: false,
    } : {
      forSell: formData.forSell,
      forRent: formData.forRent,
      openToSwap: formData.openToSwap,
      openToPickUp: formData.openToPickUp,
      openToShipping: formData.openToShipping,
    }),
    
    // Handle pickup address relation correctly - needed for both physical pickup and local services
    ...(((!isSimplifiedProduct && formData.openToPickUp) || isLocalService) ? {
      pickupAddress: formData.useShippingForPickup && formData.shippingAddress 
        ? formData.shippingAddress.id 
        : (formData.pickUpAddress ? formData.pickUpAddress.id : formData.selectedAddressId),
      useShippingForPickup: formData.useShippingForPickup || false
    } : {}),
    
    // Handle shipping address relation if needed
    ...(!isSimplifiedProduct && formData.openToShipping ? {
      shippingAddress: formData.shippingAddress 
        ? formData.shippingAddress.id
        : null
    } : {}),
    
    // Include parcel information if shipping is enabled
    ...(!isSimplifiedProduct && formData.openToShipping && formData.parcelInfo ? {
      parcelInfo: {
        length: parseFloat(formData.parcelInfo.length) || 0,
        width: parseFloat(formData.parcelInfo.width) || 0,
        height: parseFloat(formData.parcelInfo.height) || 0,
        weight: parseFloat(formData.parcelInfo.weight) || 0,
        units: formData.parcelInfo.units || { distance: 'in', mass: 'lb' }
      }
    } : {}),
    
    // Include rental info if item is for rent or is a local service
    ...((formData.forRent && !isSimplifiedProduct) || isLocalService ? {
      rentPriceAndTime: formData.rentPriceAndTime.map(rent => ({
        ...rent,
        price: parseInt(rent.price) || 0,
        currency: rent.currency || formData.currency || 'USD'
      })),
      rentDeposit: formData.requireDeposit ? (parseInt(formData.rentDeposit) || 0) : null
    } : {
      rentPriceAndTime: [],
      rentDeposit: null
    }),
    
    users_permissions_user: userId
  };
  
  // Log the data being sent to the API for debugging
  console.log('Submitting product data:', data);
  
  return data;
};

const listingSlice = createSlice({
  name: 'listing',
  initialState,
  reducers: {
    toggleProductSelection: (state, action) => {
      const productId = action.payload;
      const productIndex = state.selectedProducts.indexOf(productId);
      
      if (productIndex === -1) {
        // If product is not selected, add it
        state.selectedProducts.push(productId);
      } else {
        // If product is already selected, remove it
        state.selectedProducts.splice(productIndex, 1);
      }
    },
    toggleSelectAll: (state, action) => {
      const productIds = action.payload;
      
      if (state.selectedProducts.length === productIds.length) {
        // If all products are selected, deselect all
        state.selectedProducts = [];
      } else {
        // If some or none are selected, select all
        state.selectedProducts = [...productIds];
      }
    },
    clearSelectedProducts: (state) => {
      state.selectedProducts = [];
    },
    setDeleteModalOpen: (state, action) => {
      state.deleteModalOpen = action.payload;
    },
    setExpectedCurrencyForOrigin: (state, action) => {
      state.form.formData.expectedCurrencyForOrigin = action.payload;
    },
    updateFormData: (state, action) => {
      // First merge the new data
      const newFormData = {
        ...state.form.formData,
        ...action.payload
      };
      
      // Check if productType is being changed to 'virtual'
      if (action.payload.productType === 'virtual' && 
          state.form.formData.productType !== 'virtual') {
        // Set appropriate defaults for virtual fashion
        newFormData.forSell = true;
        newFormData.forRent = false;
        newFormData.openToSwap = false;
        newFormData.openToPickUp = false;
        newFormData.openToShipping = false;
        newFormData.stock = 999; // Set a high stock value for virtual products
      }
      
      // Check if we're changing to a digital/online product
      if (action.payload.productType === 'others' && 
          action.payload.isDigitalOnline === true && 
          !(state.form.formData.productType === 'others' && state.form.formData.isDigitalOnline === true)) {
        // Set appropriate defaults for digital/online products - similar to virtual products
        newFormData.forSell = true;
        newFormData.forRent = false;
        newFormData.openToSwap = false;
        newFormData.openToPickUp = false;
        newFormData.openToShipping = false;
        newFormData.stock = 999; // Set a high stock value
        newFormData.isLocalService = false; // Can't be both digital and local
      }
      
      // Check if we're changing to a local service
      if (action.payload.productType === 'others' && 
          action.payload.isLocalService === true && 
          !(state.form.formData.productType === 'others' && state.form.formData.isLocalService === true)) {
        // Set appropriate defaults for local services
        newFormData.forSell = false;
        newFormData.forRent = true;
        newFormData.openToSwap = false;
        newFormData.openToPickUp = true; // Need pickup address for service location
        newFormData.openToShipping = false;
        newFormData.stock = 999; // Set a high stock value for service
        newFormData.isDigitalOnline = false; // Can't be both digital and local
        
        // Initialize service radius with a default value
        newFormData.serviceRadius = newFormData.serviceRadius || 10;
        
        // Initialize rentPriceAndTime with an hourly rate if it's empty
        if (!newFormData.rentPriceAndTime || newFormData.rentPriceAndTime.length === 0) {
          newFormData.rentPriceAndTime = [{
            number: 1,
            option: 'hour',
            price: '',
            currency: newFormData.currency || 'USD'
          }];
        }
      }
      
      // Update state with potentially modified data
      state.form.formData = newFormData;
    },
    updateFieldErrors: (state, action) => {
      state.form.fieldErrors = {
        ...state.form.fieldErrors,
        ...action.payload
      };
    },
    setEditingId: (state, action) => {
      state.form.editingId = action.payload;
    },
    resetForm: (state) => {
      state.form = initialState.form;
    },
    initializeFormWithProduct: (state, action) => {
      const product = action.payload;
      
      // Update form data with product details
      state.form.formData = {
        ...state.form.formData,
        id: product.id,
        name: product.name || '',
        description: product.description || '',
        productType: product.productType || '',
        isDigitalOnline: product.isDigitalOnline || false,
        isLocalService: product.isLocalService || false,
        serviceRadius: product.serviceRadius || 10,
        category: product.category || '',
        size: product.size || '',
        customSizeDetails: product.customSizeDetails || '',
        color: product.color || '',
        condition: product.condition || '',
        material: product.material || '',
        brand: product.brand || '',
        price: product.price || '',
        currency: product.currency || 'USD',
        stock: product.stock || '',
        photos: product.photos || [],
        coverImageIndex: Math.max(0, product.coverImageIndex || 0),
        forSell: !!product.forSell,
        forRent: !!product.forRent,
        openToSwap: !!product.openToSwap,
        openToPickUp: !!product.openToPickUp,
        openToShipping: !!product.openToShipping,
        rentPriceAndTime: (product.rentPriceAndTime || []).map(rentPrice => ({
          ...rentPrice,
          currency: rentPrice.currency || product.currency || 'USD'
        })),
        requireDeposit: !!product.rentDeposit,
        rentDeposit: product.rentDeposit || '',
        
        // Handle pickup address relation correctly
        pickUpAddress: product.pickupAddress || null,
        selectedAddressId: product.pickupAddress?.id || null,
        
        // Handle shipping address relation correctly
        shippingAddress: product.shippingAddress || null,
        selectedShippingAddressId: product.shippingAddress?.id || null,
        useShippingForPickup: !!product.useShippingForPickup,
        
        // Handle parcel information
        parcelInfo: {
          length: product.parcelInfo?.length || '',
          width: product.parcelInfo?.width || '',
          height: product.parcelInfo?.height || '',
          weight: product.parcelInfo?.weight || '',
          units: product.parcelInfo?.units || { distance: 'in', mass: 'lb' }
        }
      };
      
      // Log the pickup address for debugging
      console.log('Initializing form with pickup address:', product.pickupAddress);
      console.log('Initializing form with shipping address:', product.shippingAddress);
      
      // Clear any field errors
      state.form.fieldErrors = initialState.form.fieldErrors;
    },
    normalizeFormData: (state) => {
      // Convert empty strings to 0 for rental-related fields
      state.form.formData = {
        ...state.form.formData,
        rentDeposit: state.form.formData.rentDeposit === '' ? 0 : state.form.formData.rentDeposit,
      };
    }
  },
  extraReducers: (builder) => {
    // Handle external actions
    builder
      .addCase(resetSelectedProducts, (state) => {
        state.selectedProducts = [];
      });
  }
});

export const { 
  toggleProductSelection, 
  toggleSelectAll,
  clearSelectedProducts,
  setDeleteModalOpen,
  setExpectedCurrencyForOrigin,
  updateFormData,
  updateFieldErrors,
  setEditingId,
  resetForm,
  initializeFormWithProduct,
  normalizeFormData
} = listingSlice.actions;

export default listingSlice.reducer; 